import CircularProgress from "@mui/material/CircularProgress";
import { connect } from "react-redux";
import {
    Route, Routes,
    useNavigate
} from "react-router-dom";

// theme
import { Stack } from "@mui/material";
import CheckAPIs from "./components/CheckAPIs";
import PrivateRoute from "./components/PrivateRoute";
import ChangePassword from './components/changePassword/ChangePassword';
import ToastContainer from "./components/common/ToastContainer";
import Dashboard from "./components/dashboard/Dashboard";
import Login from "./components/login/Login";
import CustomersGroupList from "./components/master/CustomersGroup/CustomersGroupList";
import BaseList from "./components/master/base/BaseList";
import CustomersList from "./components/master/customers/CustomersList";
import DeviceIdAllocationList from "./components/master/DeviceIdAllocation/DeviceIdAllocationList";
import UsersList from "./components/master/users/UsersList";
import ObservationPointAndDevicesScreen from "./components/master/weathers/ObservationPointAndDevicesScreen";
import WeathersData from "./components/master/weathers/WeathersData";
import WeathersList from "./components/master/weathers/WeathersList";
import ReportDemand from "./components/polling/ReportDemand/ReportDemand";
import DemandsDetails from "./components/polling/demands/DemandsDetails";
import AddDevice from "./components/polling/device/AddDevice";
import AddDeviceList from "./components/polling/device/AddDeviceList";
import DeviceList from "./components/polling/device/DeviceList";
import EditDevice from "./components/polling/device/EditDevice";
import DeviceJSPList from "./components/polling/deviceJSP/DeviceJSPList";
import LeaksDetails from "./components/polling/leaks/LeaksDetails";
import NotificationsDetails from "./components/polling/notifications/NotificationsDetails";
import NotifyList from "./components/polling/notify/NotifyList";
import PointsDetails from "./components/polling/points/PointsDetails";
import PollingTopicDetails from "./components/polling/pollingTopicDetails/PollingTopicDetails";
import SolarsDetails from "./components/polling/solars/SolarsDetails";
import AlarmDemandList from "./components/smonitor/AlarmDemand/AlarmDemandList";
import SettingDemandList from "./components/smonitor/SettingDemand/SettingDemandList";
import SettingSolarList from "./components/smonitor/SettingSolar/SettingSolarList";
import SmartSolarList from "./components/smonitor/SmartSolar/SmartSolarList";
import SmartDemandList from "./components/smonitor/smartDemand/SmartDemandList";
import SolarLiveStatus from "./components/solarLiveStatus/SolarLiveStatus";
import AbacList from "./components/telemetry/Abac/AbacList";
import AdList from "./components/telemetry/Ad/AdList";
import AeList from "./components/telemetry/Ae/AeList";
import AlarmList from "./components/telemetry/Alarm/AlarmList";
import BeList from "./components/telemetry/Be/BeList";
import BxList from "./components/telemetry/Bx/BxList";
import EaList from "./components/telemetry/Ea/EaList";
import EbecList from "./components/telemetry/Ebec/EbecList";
import FaList from "./components/telemetry/Fa/FaList";
import GaList from "./components/telemetry/Ga/GaList";
import Ga2List from "./components/telemetry/Ga2/Ga2List";
import Gaex1 from "./components/telemetry/Gaex1/Gaex1";
import Gaex2 from "./components/telemetry/Gaex2/Gaex2";
import Gaex3 from "./components/telemetry/Gaex3/Gaex3";
import Gaex5 from "./components/telemetry/Gaex5/Gaex5";
import GbList from "./components/telemetry/Gb/GbList";
import GcList from "./components/telemetry/Gc/GcList";
import MonthlyPowerUpdate from "./components/telemetry/MonthlyPowerUpdate";
import DemandDataList from "./components/telemetry/demandData/DemandDataList";
import { Layout } from "./layouts/Layout";
import { Path as RoutePath } from "./path";
import { ApplicationState } from "./store";
import ThemeConfig from './theme';
import Aeex1List from "./components/telemetry/Ae/Aeex1List";
import PollingTopicDetailsJSPDevice from "./components/polling/pollingTopicDetails_JSPDevice/PollingTopicDetails_JSPDevice";
import { useLocation } from "react-router-dom";
import React from "react";
import { getIDBValue, isIosDevice, isMobileDevice, setManifestURL } from "./utils";
import useResponsive from "./hooks/useResponsive";
import DemandDriveList from "./components/demandDrive/DemandDriveList";
import FeList from "./components/telemetry/Fe/FeList";
import SmartSettingMeasurementList from "./components/smonitor/SmartSettingMeasurement/SmartSettingMeasurementList";
import SmartMeasurementDisplay from "./components/smonitor/SmartSettingMeasurement/SmartMeasurementDisplay";
import PollingTopicDetails_TJ360VersionDevice from "./components/polling/pollingTopicDetails_TJ360VersionDevice/PollingTopicDetails_TJ360VersionDevice";

interface AppData {
    loading: boolean;
}

export type AppProps = AppData;

const DB_NAME = 'PWA_Storage';
const DB_VERSION = 2;
const STORE_NAME = 'NavigationState';
const KEY_NAME = 'last_pwa_path';

const setIDBValue = (value: string) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (e: any) => {
        const db = e.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
            db.createObjectStore(STORE_NAME);
        }
    };

    request.onsuccess = (e: any) => {
        const db = e.target.result;
        if (db.objectStoreNames.contains(STORE_NAME)) {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            tx.objectStore(STORE_NAME).put(value, KEY_NAME);
        }
    };
};

const App = (props: AppProps) => {
    const isMobile = useResponsive({ query: 'down', key: 'md' }) || isMobileDevice();
    const location = useLocation();
    const navigate = useNavigate();
    const isFirstLoad = React.useRef(true);

    React.useEffect(() => {
        if(isIosDevice()){
            if (isFirstLoad.current) {
                isFirstLoad.current = false;
                return;
            }
            const isIgnorePage = location.pathname.includes('login') || location.pathname.includes('changepass');
            if (!isIgnorePage) {
                if (location.pathname !== '/') {
                    const currentPath = location.pathname + location.search;
                    setIDBValue(currentPath);
                }
            }
        }
    }, [location.pathname, location.search]);

    React.useEffect(() => {
        if (isIosDevice()) {
            getIDBValue().then((lastPath) => {
                const allowedPaths = [
                    RoutePath.SolarLiveStatus,
                    RoutePath.Gaex1,
                    RoutePath.Gaex2,
                    RoutePath.Gaex3,
                    RoutePath.SmartDemandList,
                    RoutePath.SmartSolarList
                ];
                const isAllowed = lastPath && lastPath !== '/' && allowedPaths.some(p => lastPath.startsWith(p));
                const isIOS = isIosDevice();
                if(isIOS){
                    if(!lastPath){
                        navigate(RoutePath.SolarLiveStatus, { replace: true });
                        return;
                    }
                }
                if (isAllowed) {
                    navigate(lastPath, { replace: true });
                } else if (lastPath && lastPath !== '/') {
                    navigate(RoutePath.SolarLiveStatus, { replace: true });
                }
            });
        }
    }, []);
    
    React.useEffect(() => {
        if(isMobile){
            setManifestURL();
            const timer = setTimeout(() => {
                if ((window as any).lastManifestURL) {
                    URL.revokeObjectURL((window as any).lastManifestURL);
                }
                setManifestURL(); 
                (window as any).lastManifestURL = document.querySelector('#my-manifest')?.getAttribute('href') || '';
            }, 200);
            return () => clearTimeout(timer);
        }
    }, [location.pathname, location.search]);

    if (props.loading) {
        return (
            <ThemeConfig>
                <Layout>
                    <ToastContainer />
                    <Stack
                        direction="row"
                        justifyContent="center"
                        alignItems="center"
                        spacing={0}
                        width={"100%"}
                        height={"100%"}
                    >
                        <CircularProgress />
                    </Stack>
                </Layout>
            </ThemeConfig>
        );
    }
    return (
        <ThemeConfig>
            <Routes>
                <Route path="/" element={<PrivateRoute returnElement={<Layout />}></PrivateRoute>}>
                    <Route index element={<SolarLiveStatus />} />
                    <Route path={RoutePath.DemandDrive} element={<DemandDriveList />} />
                    <Route path={RoutePath.Dashboard} element={<Dashboard />} />
                    <Route path={RoutePath.AlarmList} element={<AlarmList />} />
                    <Route path={RoutePath.AbacList} element={<AbacList />} />
                    <Route path={RoutePath.AdList} element={<AdList />} />
                    <Route path={RoutePath.GbList} element={<GbList />} />
                    <Route path={RoutePath.GcList} element={<GcList />} />
                    <Route path={RoutePath.EaList} element={<EaList />} />
                    <Route path={RoutePath.EbecList} element={<EbecList />} />
                    <Route path={RoutePath.BxList} element={<BxList />} />
                    <Route path={RoutePath.BeList} element={<BeList />} />
                    <Route path={RoutePath.FeList} element={<FeList />} />
                    <Route path={RoutePath.FaList} element={<FaList />} />
                    <Route path={RoutePath.GaList} element={<GaList />} />
                    <Route path={RoutePath.Ga2List} element={<Ga2List />} />
                    <Route path={RoutePath.AeList} element={<AeList />} />
                    <Route path={RoutePath.Aeex1List} element={<Aeex1List />} />
                    <Route path={RoutePath.DemandList} element={<DemandDataList />} />
                    <Route path={RoutePath.Gaex2} element={<Gaex2 />} />
                    <Route path={RoutePath.Gaex5} element= {<Gaex5 />} />
                    <Route path={RoutePath.CustomersList} element={<CustomersList />} />
                    <Route path={RoutePath.UsersList} element={<UsersList />} />
                    <Route path={RoutePath.DeviceIdAllocation} element={<DeviceIdAllocationList />} />
                    <Route path={RoutePath.BaseList} element={<BaseList />} />
                    <Route path={RoutePath.DeviceList} element={<DeviceList />} />
                    <Route path={RoutePath.CustomersGroupList} element={<CustomersGroupList />} />
                    <Route path={RoutePath.AddDevice} element={<AddDevice />} />
                    <Route path={RoutePath.AddDeviceList} element={<AddDeviceList />} />
                    <Route path={RoutePath.EditDevice + '/:id'} element={<EditDevice />} />
                    <Route path={RoutePath.PointsEdit} element={<PointsDetails />} />
                    <Route path={RoutePath.LeaksEdit} element={<LeaksDetails />} />
                    <Route path={RoutePath.DemandsEdit} element={<DemandsDetails />} />
                    <Route path={RoutePath.SolarsEdit} element={<SolarsDetails />} />
                    <Route path={RoutePath.NotificationsEdit} element={<NotificationsDetails />} />
                    <Route path={RoutePath.Gaex1} element={<Gaex1 />} />
                    <Route path={RoutePath.Gaex3} element={<Gaex3 />} />
                    <Route path={RoutePath.Weather} element={<WeathersList />} />
                    <Route path={RoutePath.WeatherData} element={<WeathersData />} />
                    <Route path={RoutePath.ObservationPointsAndDeviceScreen + "/:id"} element={<ObservationPointAndDevicesScreen />} />
                    <Route path={'/smonitor'} >
                        <Route path={RoutePath.SmartDemandList} element={<SmartDemandList />} />
                        <Route path={RoutePath.AlarmDemandList} element={<AlarmDemandList/>} />
                        <Route path={RoutePath.SettingDemandList} element={<SettingDemandList />} />
                        <Route path={RoutePath.SmartSolarList} element={<SmartSolarList />} />
                        <Route path={RoutePath.SettingSolarList} element={<SettingSolarList />} />
                        <Route path={RoutePath.SmartSettingMeasurementList} element={<SmartSettingMeasurementList />} />
                        <Route path={RoutePath.SmartMeasurementDisplay} element={<SmartMeasurementDisplay />} />
                    </Route>
                    <Route path={'/polling'} >
                        <Route index element={<DeviceList />} />
                        <Route path={RoutePath.DeviceJSPList} element={<DeviceJSPList />} /> 
                        <Route path="detail/:id" element={<PollingTopicDetails />} />
                        <Route path={RoutePath.PollingTopicDetailJSPDevice + '/:id'} element={<PollingTopicDetailsJSPDevice />} />
                        <Route path={RoutePath.PollingTopicDetailTJ360VersionDevice + '/:id'} element={<PollingTopicDetails_TJ360VersionDevice />} />
                        <Route path={RoutePath.ReportDemand} element={<ReportDemand />} />
                    </Route>
                    <Route path={RoutePath.NotifyList} element={<NotifyList />} />
                    <Route path={RoutePath.SolarLiveStatus} element={<SolarLiveStatus />} />
                    <Route path={RoutePath.MonthlyPowerUpdate} element={<MonthlyPowerUpdate />} />
                </Route>
                <Route path="/login" element={<Login />} />
                <Route path="/changepass" element={<ChangePassword />} />
                <Route path={RoutePath.CheckAPIs} element={<CheckAPIs />} />
            </Routes>
            <ToastContainer />
        </ThemeConfig>
    );
};

export default connect((state: ApplicationState) => state.app)(App as any);
